import { DateTime } from 'luxon'
import { createSelector } from 'reselect'
import { times } from 'lodash'
import currency from 'currency.js'

import {
  filterTransactionsByType,
  getUsersTransactions,
} from '@/features/transactions/transactions.selectors'
import { centsToCurrency } from '@/utils/currencyHelpers'
import { isoToUTCDateTime } from '@/utils/dateHelpers'

export const selectPercDiffLastMonth = createSelector(
  getUsersTransactions,
  (_: unknown, currentMonth: DateTime) => currentMonth,
  (_: unknown, __: unknown, pastMonth: DateTime) => pastMonth,
  (userTransactions, currentMonth, pastMonth) => {
    const incomeTransactions = filterTransactionsByType(
      userTransactions,
      'businessIncome'
    )

    const currentDay = currentMonth.day
    const currentStart = currentMonth.toUTC().startOf('month')
    const currentEnd = currentMonth
      .toUTC()
      .set({ day: currentDay })
      .endOf('day')

    const pastDay = Math.min(pastMonth.endOf('month').day, currentDay)
    const pastStart = pastMonth.toUTC().startOf('month')
    const pastEnd = pastMonth.toUTC().set({ day: pastDay }).endOf('day')

    let currentIncome = 0
    let pastIncome = 0

    incomeTransactions.forEach((t) => {
      const d = isoToUTCDateTime(t.date)
      if (d >= currentStart && d <= currentEnd) {
        currentIncome = currency(currentIncome).add(t.amountInCents).value
      }
      if (d >= pastStart && d <= pastEnd) {
        pastIncome = currency(pastIncome).add(t.amountInCents).value
      }
    })

    if (!currentIncome || !pastIncome) {
      return null
    }

    return (currentIncome - pastIncome) / pastIncome
  }
)

export const selectMonthToDateIncomeChartData = createSelector(
  getUsersTransactions,
  (_: unknown, pastValue: 'month' | 'year') => pastValue,
  (userTransactions, pastValue) => {
    const today = DateTime.now()

    const pastDate =
      pastValue === 'month'
        ? today.minus({ months: 1 })
        : today.minus({ years: 1 })

    const currentMonthStart = today.toUTC().startOf('month')
    const currentMonthEnd = today.toUTC().endOf('month')

    const pastMonthStart = pastDate.toUTC().startOf('month')
    const pastMonthEnd = pastDate.toUTC().endOf('month')

    const maxDays = Math.max(
      today.endOf('month').day,
      pastDate.endOf('month').day
    )
    const incomeTransactions = filterTransactionsByType(
      userTransactions,
      'businessIncome'
    )
    const totalsPerDay = new Map<number, currency>()
    const pastTotalsPerDay = new Map<number, currency>()

    // Calculate totals for each day, throw into maps for lookup
    incomeTransactions.forEach((transaction) => {
      const transactionDate = isoToUTCDateTime(transaction.date)
      const day = transactionDate.day

      if (
        transactionDate >= currentMonthStart &&
        transactionDate <= currentMonthEnd
      ) {
        const currentTotal = totalsPerDay.get(day) || currency(0)
        totalsPerDay.set(
          day,
          currentTotal.add(centsToCurrency(transaction.amountInCents))
        )
      }

      if (
        transactionDate >= pastMonthStart &&
        transactionDate <= pastMonthEnd
      ) {
        const pastTotal = pastTotalsPerDay.get(day) || currency(0)
        pastTotalsPerDay.set(
          day,
          pastTotal.add(centsToCurrency(transaction.amountInCents))
        )
      }
    })

    // Calculate cumulative totals for each day
    let currentCumulative = currency(0)
    let pastCumulative = currency(0)
    return times(maxDays).map((i) => {
      const dayOfMonth = i + 1

      // Add cumulative totals for each day
      currentCumulative = currentCumulative.add(
        totalsPerDay.get(dayOfMonth) || 0
      )
      pastCumulative = pastCumulative.add(pastTotalsPerDay.get(dayOfMonth) || 0)

      return {
        current: dayOfMonth <= today.day ? currentCumulative.intValue : null,
        past: pastCumulative.intValue,
        dayOfMonth,
      }
    })
  }
)
